import Player from '@vimeo/player';
import type { ConsentStatusObject } from '@usercentrics/cmp-browser-sdk/dist/src/services/EventDispatcher/interfaces';
import { onDomReady } from './on-dom-ready';

const isMobile = () =>
  /Mobile|Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|IEMobile|Opera Mini|Tablet/i.test(
    navigator.userAgent,
  );

declare global {
  interface Navigator {
    /**
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection}
     */
    connection?: NetworkInformation;
  }
  /**
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation}
   */
  interface NetworkInformation {
    saveData: boolean;
  }
}

interface UCEvent extends Event {
  detail: ConsentStatusObject;
}

const VIDEO_PLAYING_CLASS_NAME = 'hero-video--playing';

interface VimeoConsentStatusObject extends ConsentStatusObject {
  Vimeo: boolean;
}

/**
 * Process the consent given by the user and decide whether to show the video or not
 * @param videoParentElement
 */
const handleConsent = (videoParentElement: HTMLElement) => {
  // the frame got loaded so it either shows a cookie banner or the video.
  const vimeoService = window.UC_UI?.getServicesBaseInfo().find(
    (s) => s.name === 'Vimeo',
  );

  if (!vimeoService) {
    return;
  }

  const consentHistory = vimeoService.consent.history;
  const lastHistoryEntry = consentHistory[consentHistory.length - 1];

  // Vimeo service gets "accepted" on page load.
  // But in reality, uc block is shown, which means that the player is not visible.
  if (lastHistoryEntry === undefined || lastHistoryEntry.type !== 'explicit') {
    return;
  }

  if (vimeoService.consent.status) {
    videoParentElement.classList.add(VIDEO_PLAYING_CLASS_NAME);
  }
};

const renderAndPlayVideo = (videoParentElement: HTMLElement) => {
  const videoElementMarkup = videoParentElement.dataset['video'];
  const videoContainer = videoParentElement.querySelector(
    '.hero-video__container',
  )!!;

  videoContainer.innerHTML += videoElementMarkup;

  const abortLoadListenerController = new AbortController();

  handleConsent(videoParentElement);

  // @ts-ignore
  window.addEventListener('ucEvent', (e: UCEvent) => {
    if (e.detail.type !== 'explicit') {
      return;
    }

    if (
      'Vimeo' in e.detail &&
      (e.detail as VimeoConsentStatusObject)['Vimeo']
    ) {
      videoParentElement.classList.add(VIDEO_PLAYING_CLASS_NAME);
    } else {
      videoParentElement.classList.remove(VIDEO_PLAYING_CLASS_NAME);
    }
    abortLoadListenerController.abort();
  });

  const player = new Player(
    videoParentElement.querySelector<HTMLElement>('.hero-video__video')!!,
  );

  videoParentElement.addEventListener('click', async () => {
    if (await player.getPaused()) {
      player.play();
    } else {
      player.pause();
    }
  });
};

/**
 * The hero video element to behave like a background
 * Autoplay true for desktop; False for mobile / metered connection
 * Loop after
 */
onDomReady(() => {
  document
    .querySelectorAll<HTMLElement>('div.hero-video')
    .forEach((element) => {
      if (!isMobile() && navigator.connection?.saveData !== true) {
        renderAndPlayVideo(element);
      } else {
        element.addEventListener('click', () => renderAndPlayVideo(element), {
          once: true,
        });
      }
    });
});
